import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import {
  ArrowRight, AlertTriangle, CheckCircle,
  ShieldAlert, ShieldCheck,
} from 'lucide-react';

const PROJECT_COLORS = [
  { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', bar: '#3b82f6' },
  { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', bar: '#10b981' },
  { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', bar: '#f59e0b' },
  { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', bar: '#a855f7' },
];

function getScoreColor(score) {
  if (score === 3) return 'text-green-400';
  if (score === 2) return 'text-blue-400';
  if (score === 1) return 'text-yellow-400';
  return 'text-red-400';
}

function getStatusBadge(status) {
  if (status === 'ko') return <span className="badge-ko rounded-sm">K.O.</span>;
  if (status === 'passed') return <span className="badge-pass rounded-sm">PASSED</span>;
  return <span className="badge-pending rounded-sm">PENDING</span>;
}

export function ProjectSelectionTable({ projects, selectedIds, onToggle }) {
  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid="project-selection-table">
      <table className="w-full">
        <thead>
          <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground font-bold">
            <th className="py-2.5 px-4 w-10"></th>
            <th className="text-left py-2.5 px-4">Project</th>
            <th className="text-left py-2.5 px-4">Status</th>
            <th className="text-left py-2.5 px-4">Phase 1</th>
            <th className="text-left py-2.5 px-4">Phase 2</th>
            <th className="text-left py-2.5 px-4">Created</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p, i) => {
            const isSelected = selectedIds.includes(p.id);
            const isDisabled = !isSelected && selectedIds.length >= 4;
            return (
              <tr key={p.id} data-testid={`compare-select-row-${i}`}
                onClick={() => !isDisabled && onToggle(p.id)}
                className={`border-t border-border transition-colors duration-100 cursor-pointer ${isSelected ? 'bg-blue-600/5 border-l-2 border-l-blue-500' : 'hover:bg-secondary/30'} ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <td className="py-3 px-4">
                  <Checkbox checked={isSelected} disabled={isDisabled} data-testid={`compare-checkbox-${i}`}
                    className="border-border data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
                </td>
                <td className="py-3 px-4 text-sm font-medium text-foreground">{p.name}</td>
                <td className="py-3 px-4">{getStatusBadge(p.status)}</td>
                <td className="py-3 px-4 font-mono text-sm">
                  {p.phase1_score !== null ? (
                    <span className={p.phase1_ko ? 'text-red-400' : 'text-green-400'}>{p.phase1_score}/{p.phase1_max}</span>
                  ) : '--'}
                </td>
                <td className="py-3 px-4 font-mono text-sm">
                  {p.phase2_score !== null ? (
                    <span className={p.phase2_ko ? 'text-red-400' : 'text-green-400'}>{p.phase2_score}/{p.phase2_max}</span>
                  ) : <span className="text-muted-foreground">--</span>}
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground font-mono">
                  {new Date(p.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


export function ComparisonSummaryCards({ projects }) {
  const navigate = useNavigate();
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${projects.length}, 1fr)` }} data-testid="comparison-summary-cards">
      {projects.map((p, i) => {
        const p1 = p.phase1;
        const pct = p1 ? Math.round((p1.total_score / p1.max_score) * 100) : 0;
        return (
          <div key={p.id} data-testid={`comparison-card-${i}`}
            className={`bg-card border rounded-sm p-4 ${PROJECT_COLORS[i].border} hover:border-opacity-60 transition-colors cursor-pointer`}
            onClick={() => navigate(`/projects/${p.id}`)}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-heading text-lg ${PROJECT_COLORS[i].text}`}>{p.name}</h3>
              {p.status === 'ko' && <ShieldAlert className="w-4 h-4 text-red-400" strokeWidth={1.5} />}
              {p.status === 'passed' && <ShieldCheck className="w-4 h-4 text-green-400" strokeWidth={1.5} />}
            </div>
            {p1 && (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-mono text-2xl font-medium text-foreground">{pct}%</span>
                  <span className="text-xs text-muted-foreground font-mono">{p1.total_score}/{p1.max_score}</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full score-bar-animate"
                    style={{ '--score-width': `${pct}%`, background: p1.has_ko ? '#ef4444' : PROJECT_COLORS[i].bar }} />
                </div>
                <div className="flex items-center gap-1.5">
                  {p1.has_ko
                    ? <span className="badge-ko rounded-sm text-[10px]">K.O.</span>
                    : <span className="badge-pass rounded-sm text-[10px]">PASSED</span>}
                  {p.phase2 && (
                    <span className={`text-[10px] font-mono ${p.phase2.has_ko ? 'text-red-400' : 'text-green-400'}`}>
                      P2: {p.phase2.total_score}/{p.phase2.max_score}
                    </span>
                  )}
                </div>
              </>
            )}
            <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> View detail
            </p>
          </div>
        );
      })}
    </div>
  );
}


export function ComparisonTable({ phase, criteriaList, projects }) {
  return (
    <ScrollArea className="w-full">
      <div className="border border-border rounded-sm overflow-hidden min-w-[700px]" data-testid={`comparison-table-${phase}`}>
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground font-bold">
              <th className="text-left py-2.5 px-4 w-8">#</th>
              <th className="text-left py-2.5 px-4 min-w-[200px]">Criterion</th>
              {phase === 'phase1' && <th className="text-center py-2.5 px-3 w-14">Wt</th>}
              {projects.map((p, i) => (
                <th key={p.id} className="text-center py-2.5 px-3 min-w-[80px]">
                  <span className={PROJECT_COLORS[i].text}>{p.name.length > 12 ? p.name.slice(0, 12) + '...' : p.name}</span>
                </th>
              ))}
              <th className="text-center py-2.5 px-3 w-16">Best</th>
            </tr>
          </thead>
          <tbody>
            {criteriaList.map((c, ci) => {
              const projectScores = projects.map(p => {
                const phaseData = p[phase];
                if (!phaseData) return null;
                const scoreObj = phaseData.scores.find(s => s.criteria_id === c.id);
                return scoreObj ? scoreObj.score : null;
              });
              const validScores = projectScores.filter(s => s !== null);
              const best = validScores.length > 0 ? Math.max(...validScores) : -1;

              return (
                <tr key={c.id} data-testid={`comparison-row-${c.id}`}
                  className="border-t border-border hover:bg-secondary/20 transition-colors duration-100">
                  <td className="py-2.5 px-4 text-xs text-muted-foreground font-mono">{ci + 1}</td>
                  <td className="py-2.5 px-4 text-sm text-foreground">{c.name}</td>
                  {phase === 'phase1' && <td className="py-2.5 px-3 text-center font-mono text-xs text-muted-foreground">{c.weight}x</td>}
                  {projectScores.map((score, pi) => {
                    const isBest = score !== null && score === best && validScores.filter(s => s === best).length < validScores.length;
                    const isKo = c.is_ko && score === 0;
                    return (
                      <td key={projects[pi]?.id || `score-${pi}`} className="py-2.5 px-3 text-center">
                        {score !== null ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className={`font-mono text-sm font-medium ${getScoreColor(score)} ${isBest ? 'underline decoration-dotted underline-offset-2' : ''}`}>{score}</span>
                            {isKo && <AlertTriangle className="w-3 h-3 text-red-400" strokeWidth={2} />}
                            {isBest && score > 0 && <CheckCircle className="w-3 h-3 text-green-400/50" strokeWidth={2} />}
                          </div>
                        ) : <span className="text-xs text-muted-foreground/30">--</span>}
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-3 text-center">
                    <span className={`font-mono text-xs font-bold ${getScoreColor(best)}`}>{best >= 0 ? best : '--'}</span>
                  </td>
                </tr>
              );
            })}
            {/* Total row */}
            <tr className="border-t-2 border-border bg-secondary/30">
              <td className="py-3 px-4" colSpan={phase === 'phase1' ? 3 : 2}>
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Total Score</span>
              </td>
              {projects.map(p => {
                const phaseData = p[phase];
                if (!phaseData) return <td key={p.id} className="py-3 px-3 text-center text-xs text-muted-foreground/30">--</td>;
                const pct = Math.round((phaseData.total_score / phaseData.max_score) * 100);
                let pctColor = 'text-red-400';
                if (pct >= 70) pctColor = 'text-green-400';
                else if (pct >= 40) pctColor = 'text-yellow-400';
                return (
                  <td key={p.id} className="py-3 px-3 text-center">
                    <div className="font-mono text-sm font-medium text-foreground">{phaseData.total_score}<span className="text-muted-foreground text-xs">/{phaseData.max_score}</span></div>
                    <div className={`font-mono text-xs ${pctColor}`}>{pct}%</div>
                  </td>
                );
              })}
              <td className="py-3 px-3"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </ScrollArea>
  );
}

export { PROJECT_COLORS };
